import { useMemo } from 'react';
import { useCurrentlySelectedCities } from 'state/currentLocation';
import { useCurrentRegion } from 'state/currentRegion';
import {
  useDashboardTriggers,
  useDashboardKeywords,
  useDashboardDistricts,
  useDashboardVerticals,
  useDashboardStatus,
  useDashboardStartDate,
  useDashboardEndDate,
  useBrokerData,
  useDocumentUrl,
  useRealEstateData,
  useDashboardExclusions,
  useDashboardZoneExclusions,
  useCreatedAtStart,
  useCreatedAtEnd,
  useDashboardPastProjectStage,
  useDashboardExcludePastProjectStage,
  useDashboardCompaniesState,
  useDashboardVerticalsExclusions,
  useProjectSize,
  useParcelSize,
  useWorkTypes,
  useWorkTypesExclusions,
  useDashboardCompanyKeywordsExclusions,
  useDashboardCompanyKeywords,
  useDashboardCompanyStakeholderTypesExclusions,
  useDashboardCompanyStakeholderTypes,
  useDashboardCostState,
  useDashboardCostIncludeUnknownState,
  useUserMultiRegionSelection,
  useUserStateSelection,
  useUserCountySelection,
} from 'state/dashboard';
import { useMapRadius } from 'state/map';
import { useMapBoundsState } from 'state/mapSettings';
import { useZoomQuery, useSearchQuery } from 'state/queryParams';
import {
  excludePastStagesToRaw,
  pastStagesToRaw,
  triggersToRaw,
  formatFilterStartDate,
} from 'utils/transforms';

export const useFilterToolbarState = () => {
  const [search] = useSearchQuery();
  const [companies] = useDashboardCompaniesState();
  const [districts] = useDashboardDistricts();
  const [zoneExclusions] = useDashboardZoneExclusions();
  const [verticals] = useDashboardVerticals();
  const [verticalsExclusions] = useDashboardVerticalsExclusions();
  const [triggers] = useDashboardTriggers();
  const [savedStages] = useDashboardPastProjectStage();
  const [excludeStages] = useDashboardExcludePastProjectStage();
  const [savedCost] = useDashboardCostState();
  const [includeUnknownCost] = useDashboardCostIncludeUnknownState();
  const [status] = useDashboardStatus();
  const [startDate] = useDashboardStartDate();
  const [endDate] = useDashboardEndDate();
  const [createdAtStart] = useCreatedAtStart();
  const [createdAtEnd] = useCreatedAtEnd();
  const [keywords] = useDashboardKeywords();
  const [exclusions] = useDashboardExclusions();
  const [companyKeywords] = useDashboardCompanyKeywords();
  const [companyKeywordsExclusions] = useDashboardCompanyKeywordsExclusions();
  const [realEstate] = useRealEstateData();
  const [brokerData] = useBrokerData();
  const [documentUrl] = useDocumentUrl();
  const { projectSizeMin, projectSizeMax, projectSizeIncludeUnknown } = useProjectSize();
  const [workTypes] = useWorkTypes();
  const [workTypesExclusions] = useWorkTypesExclusions();
  const { parcelSizeMin, parcelSizeMax, parcelSizeIncludeUnknown } = useParcelSize();
  const { mapBoundsAsCardinalDirections } = useMapBoundsState();
  const { radius } = useMapRadius();
  const [zoomQuery] = useZoomQuery();
  const { currentlySelectedCities } = useCurrentlySelectedCities();
  const { currentRegion } = useCurrentRegion();
  const [companyStakeholderTypes] = useDashboardCompanyStakeholderTypes();
  const [companyStakeholderTypesExclusions] =
    useDashboardCompanyStakeholderTypesExclusions();
  const [selectedRegions] = useUserMultiRegionSelection();
  const [selectedState] = useUserStateSelection();
  const [selectedCounties] = useUserCountySelection();

  const filterFieldsState = useMemo(() => {
    // all the filters, including page
    return {
      search,
      districts,
      zoneExclusions,
      verticals,
      verticalsExclusions,
      triggers: triggersToRaw(triggers),
      savedStages: pastStagesToRaw(savedStages),
      excludeStages: pastStagesToRaw(excludeStages),
      maxPrice: savedCost.max,
      minPrice: savedCost.min,
      includeUnknownCost,
      status,
      startDate,
      endDate,
      createdAtStart,
      createdAtEnd,
      keywords,
      exclusions,
      realEstate,
      brokerData,
      documentUrl,
      radius,
      cities: currentlySelectedCities,
      companies,
      projectSizeMin,
      projectSizeMax,
      projectSizeIncludeUnknown,
      parcelSizeMin,
      parcelSizeMax,
      parcelSizeIncludeUnknown,
      workTypes,
      workTypesExclusions,
      companyKeywords,
      companyKeywordsExclusions,
      currentRegion,
      companyStakeholderTypes,
      companyStakeholderTypesExclusions,
      selectedRegions,
      selectedState,
      selectedCounties,
    };
  }, [
    search,
    districts,
    zoneExclusions,
    verticals,
    verticalsExclusions,
    triggers,
    savedCost.max,
    savedCost.min,
    includeUnknownCost,
    status,
    startDate,
    endDate,
    keywords,
    realEstate,
    brokerData,
    documentUrl,
    radius,
    currentlySelectedCities,
    exclusions,
    createdAtStart,
    createdAtEnd,
    savedStages,
    excludeStages,
    companies,
    projectSizeMin,
    projectSizeMax,
    projectSizeIncludeUnknown,
    parcelSizeMin,
    parcelSizeMax,
    parcelSizeIncludeUnknown,
    workTypes,
    workTypesExclusions,
    companyKeywords,
    companyKeywordsExclusions,
    currentRegion,
    selectedRegions,
    companyStakeholderTypes,
    companyStakeholderTypesExclusions,
    selectedState,
    selectedCounties,
  ]);

  const getFiltersAsQueryParams = () => {
    const filteredDistricts = districts.map(Number).filter((number) => number !== 0);
    const filteredZoneExclusions = zoneExclusions
      .map(Number)
      .filter((number) => number !== 0);
    const transformedTriggers = triggersToRaw(triggers);
    const transformedPastStages = pastStagesToRaw(savedStages);
    const transformedExcludeStages = excludePastStagesToRaw(excludeStages);
    const formattedCreatedAtStart = formatFilterStartDate(createdAtStart);
    const formattedStartDate = formatFilterStartDate(startDate);

    return {
      districts: filteredDistricts.length > 0 ? filteredDistricts : undefined,
      zoneExclusions:
        filteredZoneExclusions.length > 0 ? filteredZoneExclusions : undefined,
      createdAtStart: formattedCreatedAtStart || undefined,
      createdAtEnd: createdAtEnd || undefined,
      verticals: verticals && verticals.length > 0 ? verticals : undefined,
      verticalsExclusions:
        verticalsExclusions && verticalsExclusions.length > 0
          ? verticalsExclusions
          : undefined,
      triggers: transformedTriggers.length > 0 ? transformedTriggers : undefined,
      past_project_stage:
        transformedPastStages.length > 0 ? transformedPastStages : undefined,
      exclude_past_project_stage:
        transformedExcludeStages.length > 0 ? transformedExcludeStages : undefined,
      min_project_cost: savedCost.min ? Number(savedCost.min) : undefined,
      max_project_cost: savedCost.max ? Number(savedCost.max) : undefined,
      include_unknown_cost: !!includeUnknownCost,
      status: status || undefined,
      keywords: keywords || undefined,
      startDate: formattedStartDate || undefined,
      endDate: endDate || undefined,
      real_estate_data_available: !!realEstate,
      broker_data_available: !!brokerData,
      document_url_available: !!documentUrl,
      exclusions: exclusions || undefined,
      companies: companies && companies.length > 0 ? companies : undefined,
      project_size_min: projectSizeMin || undefined,
      project_size_max: projectSizeMax || undefined,
      project_size_include_unknown: !!projectSizeIncludeUnknown,
      parcel_size_min: parcelSizeMin || undefined,
      parcel_size_max: parcelSizeMax || undefined,
      parcel_size_include_unknown: !!parcelSizeIncludeUnknown,
      work_types: workTypes && workTypes.length > 0 ? workTypes : undefined,
      work_types_exclusions:
        workTypesExclusions && workTypesExclusions.length > 0
          ? workTypesExclusions
          : undefined,
      company_keywords: companyKeywords || undefined,
      company_keywords_exclusions: companyKeywordsExclusions || undefined,
      city_ids:
        currentlySelectedCities && currentlySelectedCities.length > 0
          ? currentlySelectedCities
          : undefined,
      mercator_metropolitan_id: currentRegion || undefined,
      mercator_metropolitan_ids: selectedRegions || undefined,
      company_stakeholder_types: companyStakeholderTypes || undefined,
      company_stakeholder_types_exclusions:
        companyStakeholderTypesExclusions || undefined,
      county_codes:
        selectedCounties && selectedCounties.length > 0 ? selectedCounties : undefined,
    };
  };

  const getMapStateAsQueryParams = () => {
    return {
      pathOrBounds: mapBoundsAsCardinalDirections || '',
      search: search && search.length > 0 ? search : undefined,
      zoomQuery: zoomQuery || undefined,
      radius: radius || undefined,
    };
  };

  return {
    filterFieldsState,
    getFiltersAsQueryParams,
    getMapStateAsQueryParams,
  };
};
